import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState(''); // For demo only

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();
      
      if (res.ok) {
        setSent(true);
        // Demo: store the token for easy testing
        if (data.reset_token) {
          setResetToken(data.reset_token);
        }
      } else {
        setError(data.detail || 'Something went wrong');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9FAFB] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="https://framerusercontent.com/images/OQmEVkdu6X8me8F3dTcVl4xiKY.png" 
              alt="BrightLabs" 
              className="h-10 mx-auto"
            />
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-xl p-8">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h1 className="text-2xl font-bold text-[#111111]">Forgot Password?</h1>
                <p className="text-[#6B7280] mt-2">
                  No worries! Enter your email and we'll send you reset instructions.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl text-[#111111] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED]"
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 gradient-btn text-white font-semibold rounded-xl transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-[#28C840]" />
                </div>
                <h1 className="text-2xl font-bold text-[#111111]">Check Your Email</h1>
                <p className="text-[#6B7280] mt-2 mb-6">
                  If an account exists for <span className="font-medium text-[#111111]">{email}</span>, 
                  you'll receive a password reset link shortly.
                </p>

                {/* Demo: Show reset link for testing */}
                {resetToken && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-left mb-6">
                    <p className="text-xs text-yellow-700 font-medium uppercase mb-2">Demo Mode - Reset Link:</p>
                    <Link
                      to={`/reset-password?token=${resetToken}`}
                      className="text-sm text-[#7C3AED] hover:underline break-all"
                    >
                      Click here to reset your password
                    </Link>
                    <p className="text-xs text-yellow-600 mt-2">
                      (In production, this link would be sent via email)
                    </p>
                  </div>
                )}

                <p className="text-sm text-[#9CA3AF]">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => {
                      setSent(false);
                      setResetToken('');
                    }}
                    className="text-[#7C3AED] hover:underline"
                  >
                    try again
                  </button>
                </p>
              </div>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#111111] transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
